import { Loader2, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { AdminState } from "./AdminState";
import { adminPost, type SettingsField, type SettingsGroup, useAdminQuery } from "./adminApi";

type SettingsResponse = {
  group: SettingsGroup;
  ok: true;
};

export function SettingsPanel({ group }: { group: string }) {
  const encodedGroup = encodeURIComponent(group);
  const settings = useAdminQuery<SettingsResponse>(`/api/admin/settings?group=${encodedGroup}`);
  const [draft, setDraft] = useState<Record<string, boolean | number | string>>({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: "critical" | "success"; text: string }>();
  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(settings.data?.group.values ?? {}),
    [draft, settings.data?.group.values],
  );

  useEffect(() => {
    if (settings.data?.group.values) setDraft(settings.data.group.values);
  }, [settings.data?.group.values]);

  async function saveSettings() {
    setBusy(true);
    setNotice(undefined);
    try {
      const response = await adminPost<SettingsResponse>(`/api/admin/settings/${encodedGroup}`, { values: draft });
      setDraft(response.group.values);
      setNotice({ tone: "success", text: `${response.group.label} saved.` });
      await settings.reload();
    } catch (error) {
      setNotice({ tone: "critical", text: error instanceof Error ? error.message : "Settings could not be saved." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <AdminState
      status={settings.status}
      error={settings.error}
      onRetry={() => void settings.reload()}
      emptyText="Settings are created automatically after a store is installed."
    >
      <section className="wr-settings-grid" aria-label={`${settings.data?.group.label ?? group} settings`}>
        <div className="wr-panel wr-settings-intro">
          <div>
            <h2>{settings.data?.group.label ?? group}</h2>
            <p>{settings.data?.group.description}</p>
            <div className="wr-settings-status" aria-live="polite">
              <span className={isDirty ? "wr-settings-status__dirty" : "wr-settings-status__saved"}>
                {isDirty ? "Unsaved changes" : "Saved"}
              </span>
              {settings.data?.group.updatedAt ? <small>Last saved {formatDate(settings.data.group.updatedAt)}</small> : null}
            </div>
          </div>
          <button className="wr-button" type="button" disabled={busy || !isDirty} onClick={() => void saveSettings()}>
            {busy ? <Loader2 className="wr-spin" size={15} aria-hidden /> : <Save size={15} aria-hidden />}
            Save changes
          </button>
        </div>
        {notice ? <div className={`wr-notice wr-notice--${notice.tone} wr-toast`} role="status">{notice.text}</div> : null}
        {settings.data?.group.fields.map((field) => (
          <SettingRow
            key={field.name}
            field={field}
            value={draft[field.name] ?? field.defaultValue}
            onChange={(value) => setDraft((current) => ({ ...current, [field.name]: value }))}
          />
        ))}
      </section>
    </AdminState>
  );
}

function SettingRow({
  field,
  onChange,
  value,
}: {
  field: SettingsField;
  onChange: (value: boolean | number | string) => void;
  value: boolean | number | string;
}) {
  return (
    <div className="wr-setting-row">
      <div>
        <h3>{field.label}</h3>
        <p>{field.help}</p>
        <small>{field.type === "boolean" ? "Toggle setting" : `${field.type} setting`}</small>
      </div>
      <div className="wr-panel">{renderControl(field, value, onChange)}</div>
    </div>
  );
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function renderControl(
  field: SettingsField,
  value: boolean | number | string,
  onChange: (value: boolean | number | string) => void,
) {
  if (field.type === "boolean") {
    return (
      <label className="wr-switch">
        <input type="checkbox" checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} />
        <span>{Boolean(value) ? "Enabled" : "Disabled"}</span>
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <select value={String(value)} onChange={(event) => onChange(event.target.value)}>
        {field.options?.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      type={field.type === "number" ? "number" : field.type}
      value={String(value)}
      onChange={(event) => onChange(field.type === "number" ? Number(event.target.value) : event.target.value)}
    />
  );
}
